import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Settings,
  Save,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  Globe,
  Mail,
  Bell,
  Shield,
  Database,
  Palette,
  FileText,
  RefreshCw,
  Upload,
  Download,
  Zap,
  Phone,
  Building2,
  EyeIcon,
  EyeOffIcon,
} from "lucide-react";
import { adminService } from "../../api/services/adminService";
import { useToast } from "../../components/common/ThemeToaster";

const SettingsManagement = () => {
  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasPermission, setHasPermission] = useState(false);
  const { success: toastSuccess, error: toastError } = useToast();

  // Settings state
  const [settings, setSettings] = useState({
    general: {
      siteName: "TradeBridge",
      siteUrl: "https://tradebridge.com",
      siteEmail: "info@tradebridge.com",
      sitePhone: "+1 (555) 123-4567",
      siteAddress: "123 Commerce Street, New York, NY 10001",
      timezone: "America/New_York",
      dateFormat: "MM/DD/YYYY",
      timeFormat: "12h",
      language: "en",
      enableMaintenance: false,
      maintenanceMessage:
        "We're currently performing maintenance. Please check back soon.",
    },
    appearance: {
      theme: "system",
      primaryColor: "#3b82f6",
      secondaryColor: "#8b5cf6",
      accentColor: "#ec4899",
      borderRadius: "medium",
      fontFamily: "inter",
      fontSize: "medium",
      layout: "default",
      sidebarPosition: "left",
      enableAnimations: true,
      enableGlassmorphism: true,
      showGradients: true,
    },
    email: {
      smtpHost: "smtp.gmail.com",
      smtpPort: "587",
      smtpSecure: "tls",
      smtpUser: "noreply@tradebridge.com",
      smtpPassword: "",
      fromEmail: "noreply@tradebridge.com",
      fromName: "TradeBridge",
      replyTo: "support@tradebridge.com",
      enableEmailNotifications: true,
      emailSignature: "Best regards,\nThe TradeBridge Team",
    },
    notifications: {
      emailNotifications: true,
      pushNotifications: false,
      notifyNewUser: true,
      notifyNewOrder: true,
      notifyNewMessage: true,
      notifyNewRequest: true,
      notifySystemAlert: true,
    },
    security: {
      twoFactorAuth: false,
      passwordPolicy: "strong",
      sessionTimeout: 30,
      maxLoginAttempts: 5,
      lockoutDuration: 15,
      forceHttps: true,
      enableRateLimiting: true,
      rateLimitMaxRequests: 100,
      rateLimitWindow: 15,
      allowedIps: "",
      blockedIps: "",
    },
    localization: {
      defaultCountry: "US",
      defaultCurrency: "USD",
      currencyFormat: "symbol",
      currencyPosition: "before",
      thousandSeparator: ",",
      decimalSeparator: ".",
      decimalPlaces: 2,
      weightUnit: "kg",
      dimensionUnit: "cm",
      timezone: "America/New_York",
      dateFormat: "MM/DD/YYYY",
      timeFormat: "12h",
      firstDayOfWeek: "monday",
      supportedLanguages: ["en", "es", "fr", "de"],
    },
    seo: {
      metaTitle: "TradeBridge - Enterprise Export Management",
      metaDescription:
        "Connect with trusted suppliers worldwide. Source quality products in bulk with competitive pricing.",
      metaKeywords: "wholesale, export, import, trade, business, suppliers",
      ogImage: "",
      twitterCard: "summary_large_image",
      twitterHandle: "@tradebridge",
      googleAnalyticsId: "",
      enableSitemap: true,
      enableRobotsTxt: true,
      canonicalUrl: "https://tradebridge.com",
    },
    performance: {
      enableCaching: true,
      cacheTTL: 3600,
      enableCompression: true,
      enableMinification: true,
      enableLazyLoading: true,
      enablePagination: true,
      itemsPerPage: 20,
      enableImageOptimization: true,
      imageQuality: 80,
    },
    backup: {
      enableAutoBackup: true,
      backupFrequency: "daily",
      backupTime: "02:00",
      backupRetention: 30,
      lastBackup: null,
      backupSize: "0 MB",
    },
  });

  const [showPassword, setShowPassword] = useState({});

  // Load settings from localStorage on mount
  useEffect(() => {
    checkPermissions();
    loadSettings();
  }, []);

  const checkPermissions = async () => {
    try {
      const response = await adminService.checkPermission("manage_settings");
      setHasPermission(response?.data?.hasPermission || false);
    } catch (err) {
      console.error("Error checking permissions:", err);
      setHasPermission(false);
    }
  };

  const loadSettings = () => {
    try {
      setLoading(true);
      const savedSettings = localStorage.getItem("app_settings");
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings((prev) => ({
          ...prev,
          ...parsedSettings,
        }));
      }
    } catch (err) {
      console.error("Error loading settings:", err);
      setError("Failed to load settings");
      toastError("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!hasPermission) {
      setError("You don't have permission to modify settings");
      toastError("You don't have permission to modify settings");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      localStorage.setItem("app_settings", JSON.stringify(settings));
      await new Promise((resolve) => setTimeout(resolve, 500));
      setSuccess("Settings saved successfully!");
      toastSuccess("Settings saved successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Error saving settings:", err);
      setError("Failed to save settings. Please try again.");
      toastError("Failed to save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!hasPermission) {
      setError("You don't have permission to reset settings");
      toastError("You don't have permission to reset settings");
      return;
    }

    if (
      !window.confirm("Are you sure you want to reset all settings to default?")
    ) {
      return;
    }

    localStorage.removeItem("app_settings");
    window.location.reload();
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    const exportFileDefaultName = `settings-${new Date().toISOString().split("T")[0]}.json`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();

    setSuccess("Settings exported successfully!");
    toastSuccess("Settings exported successfully!");
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!hasPermission) {
      setError("You don't have permission to import settings");
      toastError("You don't have permission to import settings");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedSettings = JSON.parse(e.target.result);
        setSettings((prev) => ({
          ...prev,
          ...importedSettings,
        }));
        localStorage.setItem("app_settings", JSON.stringify(importedSettings));
        setSuccess("Settings imported successfully!");
        toastSuccess("Settings imported successfully!");
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        console.error("Error importing settings:", err);
        setError("Invalid settings file format");
        toastError("Invalid settings file format");
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const handleInputChange = (section, field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleNestedInputChange = (section, group, field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [group]: {
          ...prev[section][group],
          [field]: value,
        },
      },
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const tabs = [
    {
      id: "general",
      label: "General",
      icon: Settings,
      description: "Basic site information and settings",
    },
    {
      id: "appearance",
      label: "Appearance",
      icon: Palette,
      description: "Customize the look and feel",
    },
    {
      id: "email",
      label: "Email",
      icon: Mail,
      description: "Email server and notification settings",
    },
    {
      id: "notifications",
      label: "Notifications",
      icon: Bell,
      description: "Configure notification preferences",
    },
    {
      id: "security",
      label: "Security",
      icon: Shield,
      description: "Security and authentication settings",
    },
    {
      id: "localization",
      label: "Localization",
      icon: Globe,
      description: "Regional and language settings",
    },
    {
      id: "seo",
      label: "SEO",
      icon: FileText,
      description: "Search engine optimization",
    },
    {
      id: "performance",
      label: "Performance",
      icon: Zap,
      description: "Performance optimization",
    },
    {
      id: "backup",
      label: "Backup",
      icon: Database,
      description: "Backup configuration",
    },
  ];

  const timezones = [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "Europe/London",
    "Europe/Paris",
    "Europe/Berlin",
    "Asia/Dubai",
    "Asia/Kolkata",
    "Asia/Shanghai",
    "Asia/Tokyo",
    "Australia/Sydney",
  ];

  const languages = [
    { code: "en", name: "English" },
    { code: "es", name: "Spanish" },
    { code: "fr", name: "French" },
    { code: "de", name: "German" },
    { code: "it", name: "Italian" },
    { code: "pt", name: "Portuguese" },
    { code: "ru", name: "Russian" },
    { code: "zh", name: "Chinese" },
    { code: "ja", name: "Japanese" },
  ];

  const currencies = [
    { code: "USD", name: "US Dollar", symbol: "$" },
    { code: "EUR", name: "Euro", symbol: "€" },
    { code: "GBP", name: "British Pound", symbol: "£" },
    { code: "JPY", name: "Japanese Yen", symbol: "¥" },
    { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
    { code: "INR", name: "Indian Rupee", symbol: "₹" },
  ];

  const SectionCard = ({ title, description, children }) => (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800/50 dark:to-gray-900">
        <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {description}
          </p>
        )}
      </div>
      <div className="p-6 space-y-5">{children}</div>
    </div>
  );

  const InputField = ({
    label,
    name,
    value,
    onChange,
    type = "text",
    help,
    required,
    placeholder,
    icon: Icon,
  }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2">
            <Icon size={16} className="text-gray-400" />
          </div>
        )}
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          disabled={!hasPermission}
          placeholder={placeholder}
          className={`w-full ${Icon ? "pl-10" : "px-4"} py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
        />
      </div>
      {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
    </div>
  );

  const SelectField = ({
    label,
    name,
    value,
    onChange,
    options,
    help,
    required,
  }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        disabled={!hasPermission}
        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
    </div>
  );

  const ToggleField = ({ label, name, checked, onChange, description }) => (
    <div className="flex items-start justify-between py-2">
      <div className="flex-1 pr-4">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </p>
        {description && (
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange({ target: { name, value: !checked } })}
        disabled={!hasPermission}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
          checked ? "bg-primary-600" : "bg-gray-200 dark:bg-gray-700"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
            checked ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );

  const TextAreaField = ({
    label,
    name,
    value,
    onChange,
    rows = 3,
    help,
    placeholder,
  }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={rows}
        disabled={!hasPermission}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all font-mono text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      />
      {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
    </div>
  );

  const PasswordField = ({ label, name, value, onChange, help }) => {
    const isVisible = showPassword[name];
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
        </label>
        <div className="relative">
          <input
            type={isVisible ? "text" : "password"}
            name={name}
            value={value}
            onChange={onChange}
            disabled={!hasPermission}
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all pr-10 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility(name)}
            disabled={!hasPermission}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            {isVisible ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
          </button>
        </div>
        {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
      </div>
    );
  };

  const ColorField = ({ label, name, value, onChange }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          name={name}
          value={value}
          onChange={onChange}
          disabled={!hasPermission}
          className="w-12 h-12 rounded-xl border border-gray-200 dark:border-gray-700 cursor-pointer disabled:opacity-50"
        />
        <input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          disabled={!hasPermission}
          className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-mono"
        />
      </div>
    </div>
  );

  // Render functions for each tab
  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <SectionCard
        title="Site Information"
        description="Basic information about your site"
      >
        <InputField
          label="Site Name"
          name="siteName"
          value={settings.general.siteName}
          onChange={handleInputChange("general", "siteName")}
          required
          icon={Building2}
        />
        <InputField
          label="Site URL"
          name="siteUrl"
          value={settings.general.siteUrl}
          onChange={handleInputChange("general", "siteUrl")}
          type="url"
          required
          help="Your website's full URL including https://"
          icon={Globe}
        />
        <InputField
          label="Site Email"
          name="siteEmail"
          value={settings.general.siteEmail}
          onChange={handleInputChange("general", "siteEmail")}
          type="email"
          required
          icon={Mail}
        />
        <InputField
          label="Site Phone"
          name="sitePhone"
          value={settings.general.sitePhone}
          onChange={handleInputChange("general", "sitePhone")}
          help="Include country code for international numbers"
          icon={Phone}
        />
        <TextAreaField
          label="Site Address"
          name="siteAddress"
          value={settings.general.siteAddress}
          onChange={handleInputChange("general", "siteAddress")}
          rows={2}
          placeholder="123 Commerce Street, New York, NY 10001"
        />
      </SectionCard>

      <SectionCard title="Date & Time" description="Regional time settings">
        <SelectField
          label="Timezone"
          name="timezone"
          value={settings.general.timezone}
          onChange={handleInputChange("general", "timezone")}
          options={timezones.map((tz) => ({ value: tz, label: tz }))}
        />
        <div className="grid grid-cols-2 gap-4">
          <SelectField
            label="Date Format"
            name="dateFormat"
            value={settings.general.dateFormat}
            onChange={handleInputChange("general", "dateFormat")}
            options={[
              { value: "MM/DD/YYYY", label: "MM/DD/YYYY" },
              { value: "DD/MM/YYYY", label: "DD/MM/YYYY" },
              { value: "YYYY-MM-DD", label: "YYYY-MM-DD" },
            ]}
          />
          <SelectField
            label="Time Format"
            name="timeFormat"
            value={settings.general.timeFormat}
            onChange={handleInputChange("general", "timeFormat")}
            options={[
              { value: "12h", label: "12-hour (12:00 PM)" },
              { value: "24h", label: "24-hour (14:00)" },
            ]}
          />
        </div>
        <SelectField
          label="Default Language"
          name="language"
          value={settings.general.language}
          onChange={handleInputChange("general", "language")}
          options={languages.map((lang) => ({
            value: lang.code,
            label: lang.name,
          }))}
        />
      </SectionCard>

      <SectionCard
        title="Maintenance Mode"
        description="Put your site in maintenance mode"
      >
        <ToggleField
          label="Enable Maintenance Mode"
          name="enableMaintenance"
          checked={settings.general.enableMaintenance}
          onChange={handleInputChange("general", "enableMaintenance")}
          description="When enabled, only administrators can access the site"
        />
        {settings.general.enableMaintenance && (
          <TextAreaField
            label="Maintenance Message"
            name="maintenanceMessage"
            value={settings.general.maintenanceMessage}
            onChange={handleInputChange("general", "maintenanceMessage")}
            rows={3}
            placeholder="We're currently performing maintenance. Please check back soon."
          />
        )}
      </SectionCard>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="space-y-6">
      <SectionCard title="Theme" description="Customize the look and feel">
        <SelectField
          label="Theme Mode"
          name="theme"
          value={settings.appearance.theme}
          onChange={handleInputChange("appearance", "theme")}
          options={[
            { value: "light", label: "Light" },
            { value: "dark", label: "Dark" },
            { value: "system", label: "System (Follow OS)" },
          ]}
        />

        <div className="grid grid-cols-2 gap-4">
          <ColorField
            label="Primary Color"
            name="primaryColor"
            value={settings.appearance.primaryColor}
            onChange={handleInputChange("appearance", "primaryColor")}
          />
          <ColorField
            label="Secondary Color"
            name="secondaryColor"
            value={settings.appearance.secondaryColor}
            onChange={handleInputChange("appearance", "secondaryColor")}
          />
          <ColorField
            label="Accent Color"
            name="accentColor"
            value={settings.appearance.accentColor}
            onChange={handleInputChange("appearance", "accentColor")}
          />
        </div>
      </SectionCard>

      <SectionCard title="Typography" description="Font and text settings">
        <SelectField
          label="Font Family"
          name="fontFamily"
          value={settings.appearance.fontFamily}
          onChange={handleInputChange("appearance", "fontFamily")}
          options={[
            { value: "inter", label: "Inter" },
            { value: "poppins", label: "Poppins" },
            { value: "roboto", label: "Roboto" },
            { value: "opensans", label: "Open Sans" },
          ]}
        />
        <SelectField
          label="Font Size"
          name="fontSize"
          value={settings.appearance.fontSize}
          onChange={handleInputChange("appearance", "fontSize")}
          options={[
            { value: "small", label: "Small" },
            { value: "medium", label: "Medium" },
            { value: "large", label: "Large" },
          ]}
        />
        <SelectField
          label="Border Radius"
          name="borderRadius"
          value={settings.appearance.borderRadius}
          onChange={handleInputChange("appearance", "borderRadius")}
          options={[
            { value: "none", label: "None" },
            { value: "small", label: "Small" },
            { value: "medium", label: "Medium" },
            { value: "large", label: "Large" },
          ]}
        />
      </SectionCard>

      <SectionCard title="Layout" description="Page layout preferences">
        <SelectField
          label="Layout Style"
          name="layout"
          value={settings.appearance.layout}
          onChange={handleInputChange("appearance", "layout")}
          options={[
            { value: "default", label: "Default" },
            { value: "compact", label: "Compact" },
          ]}
        />
        <SelectField
          label="Sidebar Position"
          name="sidebarPosition"
          value={settings.appearance.sidebarPosition}
          onChange={handleInputChange("appearance", "sidebarPosition")}
          options={[
            { value: "left", label: "Left" },
            { value: "right", label: "Right" },
          ]}
        />
      </SectionCard>

      <SectionCard title="Effects" description="Visual effects and animations">
        <ToggleField
          label="Enable Animations"
          name="enableAnimations"
          checked={settings.appearance.enableAnimations}
          onChange={handleInputChange("appearance", "enableAnimations")}
          description="Smooth transitions and animations throughout the site"
        />
        <ToggleField
          label="Enable Glassmorphism"
          name="enableGlassmorphism"
          checked={settings.appearance.enableGlassmorphism}
          onChange={handleInputChange("appearance", "enableGlassmorphism")}
          description="Modern glass-like blur effects"
        />
        <ToggleField
          label="Show Gradients"
          name="showGradients"
          checked={settings.appearance.showGradients}
          onChange={handleInputChange("appearance", "showGradients")}
          description="Use gradient backgrounds in UI elements"
        />
      </SectionCard>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="space-y-6">
      <SectionCard
        title="SMTP Configuration"
        description="Email server settings"
      >
        <InputField
          label="SMTP Host"
          name="smtpHost"
          value={settings.email.smtpHost}
          onChange={handleInputChange("email", "smtpHost")}
          required
        />
        <div className="grid grid-cols-2 gap-4">
          <InputField
            label="SMTP Port"
            name="smtpPort"
            value={settings.email.smtpPort}
            onChange={handleInputChange("email", "smtpPort")}
            type="number"
            required
          />
          <SelectField
            label="SMTP Secure"
            name="smtpSecure"
            value={settings.email.smtpSecure}
            onChange={handleInputChange("email", "smtpSecure")}
            options={[
              { value: "none", label: "None" },
              { value: "tls", label: "TLS" },
              { value: "ssl", label: "SSL" },
            ]}
          />
        </div>
        <InputField
          label="SMTP Username"
          name="smtpUser"
          value={settings.email.smtpUser}
          onChange={handleInputChange("email", "smtpUser")}
          required
        />
        <PasswordField
          label="SMTP Password"
          name="smtpPassword"
          value={settings.email.smtpPassword}
          onChange={handleInputChange("email", "smtpPassword")}
          help="Leave blank to keep current password"
        />
      </SectionCard>

      <SectionCard
        title="Email Settings"
        description="Default email addresses and signatures"
      >
        <InputField
          label="From Email"
          name="fromEmail"
          value={settings.email.fromEmail}
          onChange={handleInputChange("email", "fromEmail")}
          type="email"
          required
          help="Default sender email address"
        />
        <InputField
          label="From Name"
          name="fromName"
          value={settings.email.fromName}
          onChange={handleInputChange("email", "fromName")}
          required
          help="Default sender name"
        />
        <InputField
          label="Reply-To Email"
          name="replyTo"
          value={settings.email.replyTo}
          onChange={handleInputChange("email", "replyTo")}
          type="email"
          help="Address for replies (optional)"
        />
        <TextAreaField
          label="Email Signature"
          name="emailSignature"
          value={settings.email.emailSignature}
          onChange={handleInputChange("email", "emailSignature")}
          rows={4}
          help="HTML signature appended to all outgoing emails"
        />
        <ToggleField
          label="Enable Email Notifications"
          name="enableEmailNotifications"
          checked={settings.email.enableEmailNotifications}
          onChange={handleInputChange("email", "enableEmailNotifications")}
        />
      </SectionCard>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <SectionCard
        title="Notification Channels"
        description="Where to send notifications"
      >
        <ToggleField
          label="Email Notifications"
          name="emailNotifications"
          checked={settings.notifications.emailNotifications}
          onChange={handleInputChange("notifications", "emailNotifications")}
        />
        <ToggleField
          label="Push Notifications"
          name="pushNotifications"
          checked={settings.notifications.pushNotifications}
          onChange={handleInputChange("notifications", "pushNotifications")}
        />
      </SectionCard>

      <SectionCard
        title="Notification Events"
        description="Which events trigger notifications"
      >
        <ToggleField
          label="New User Registration"
          name="notifyNewUser"
          checked={settings.notifications.notifyNewUser}
          onChange={handleInputChange("notifications", "notifyNewUser")}
        />
        <ToggleField
          label="New Order"
          name="notifyNewOrder"
          checked={settings.notifications.notifyNewOrder}
          onChange={handleInputChange("notifications", "notifyNewOrder")}
        />
        <ToggleField
          label="New Message"
          name="notifyNewMessage"
          checked={settings.notifications.notifyNewMessage}
          onChange={handleInputChange("notifications", "notifyNewMessage")}
        />
        <ToggleField
          label="New Request"
          name="notifyNewRequest"
          checked={settings.notifications.notifyNewRequest}
          onChange={handleInputChange("notifications", "notifyNewRequest")}
        />
        <ToggleField
          label="System Alert"
          name="notifySystemAlert"
          checked={settings.notifications.notifySystemAlert}
          onChange={handleInputChange("notifications", "notifySystemAlert")}
        />
      </SectionCard>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <SectionCard
        title="Authentication"
        description="Login and session settings"
      >
        <ToggleField
          label="Two-Factor Authentication"
          name="twoFactorAuth"
          checked={settings.security.twoFactorAuth}
          onChange={handleInputChange("security", "twoFactorAuth")}
          description="Require 2FA for all admin accounts"
        />
        <div className="grid grid-cols-2 gap-4">
          <InputField
            label="Session Timeout (minutes)"
            name="sessionTimeout"
            value={settings.security.sessionTimeout}
            onChange={handleInputChange("security", "sessionTimeout")}
            type="number"
            min="5"
            max="1440"
          />
          <InputField
            label="Max Login Attempts"
            name="maxLoginAttempts"
            value={settings.security.maxLoginAttempts}
            onChange={handleInputChange("security", "maxLoginAttempts")}
            type="number"
            min="3"
            max="10"
          />
        </div>
        <InputField
          label="Lockout Duration (minutes)"
          name="lockoutDuration"
          value={settings.security.lockoutDuration}
          onChange={handleInputChange("security", "lockoutDuration")}
          type="number"
          min="5"
          max="60"
        />
      </SectionCard>

      <SectionCard title="Password Policy" description="Password requirements">
        <SelectField
          label="Password Strength"
          name="passwordPolicy"
          value={settings.security.passwordPolicy}
          onChange={handleInputChange("security", "passwordPolicy")}
          options={[
            { value: "basic", label: "Basic (min 6 characters)" },
            { value: "medium", label: "Medium (min 8 characters, mixed case)" },
            {
              value: "strong",
              label: "Strong (min 10 characters, mixed case, numbers, symbols)",
            },
          ]}
        />
      </SectionCard>

      <SectionCard
        title="Security Headers"
        description="HTTP security settings"
      >
        <ToggleField
          label="Force HTTPS"
          name="forceHttps"
          checked={settings.security.forceHttps}
          onChange={handleInputChange("security", "forceHttps")}
          description="Redirect all HTTP traffic to HTTPS"
        />
        <ToggleField
          label="Enable Rate Limiting"
          name="enableRateLimiting"
          checked={settings.security.enableRateLimiting}
          onChange={handleInputChange("security", "enableRateLimiting")}
        />
        {settings.security.enableRateLimiting && (
          <div className="grid grid-cols-2 gap-4 mt-3">
            <InputField
              label="Max Requests"
              name="rateLimitMaxRequests"
              value={settings.security.rateLimitMaxRequests}
              onChange={handleInputChange("security", "rateLimitMaxRequests")}
              type="number"
              min="10"
              max="1000"
            />
            <InputField
              label="Time Window (minutes)"
              name="rateLimitWindow"
              value={settings.security.rateLimitWindow}
              onChange={handleInputChange("security", "rateLimitWindow")}
              type="number"
              min="1"
              max="60"
            />
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="IP Restrictions"
        description="Allow or block IP addresses"
      >
        <TextAreaField
          label="Allowed IPs"
          name="allowedIps"
          value={settings.security.allowedIps}
          onChange={handleInputChange("security", "allowedIps")}
          rows={3}
          placeholder="Enter one IP per line"
          help="Leave empty to allow all IPs"
        />
        <TextAreaField
          label="Blocked IPs"
          name="blockedIps"
          value={settings.security.blockedIps}
          onChange={handleInputChange("security", "blockedIps")}
          rows={3}
          placeholder="Enter one IP per line"
          help="Blocked IPs will be denied access"
        />
      </SectionCard>
    </div>
  );

  const renderLocalizationSettings = () => (
    <div className="space-y-6">
      <SectionCard
        title="Regional Settings"
        description="Country and currency preferences"
      >
        <SelectField
          label="Default Country"
          name="defaultCountry"
          value={settings.localization.defaultCountry}
          onChange={handleInputChange("localization", "defaultCountry")}
          options={[
            { value: "US", label: "United States" },
            { value: "GB", label: "United Kingdom" },
            { value: "CA", label: "Canada" },
            { value: "AU", label: "Australia" },
            { value: "DE", label: "Germany" },
            { value: "FR", label: "France" },
            { value: "JP", label: "Japan" },
          ]}
        />
        <SelectField
          label="Default Currency"
          name="defaultCurrency"
          value={settings.localization.defaultCurrency}
          onChange={handleInputChange("localization", "defaultCurrency")}
          options={currencies.map((c) => ({
            value: c.code,
            label: `${c.name} (${c.symbol})`,
          }))}
        />
      </SectionCard>

      <SectionCard
        title="Currency Format"
        description="How currency values are displayed"
      >
        <SelectField
          label="Currency Format"
          name="currencyFormat"
          value={settings.localization.currencyFormat}
          onChange={handleInputChange("localization", "currencyFormat")}
          options={[
            { value: "symbol", label: "Symbol ($100)" },
            { value: "code", label: "Code (USD 100)" },
          ]}
        />
        <SelectField
          label="Currency Position"
          name="currencyPosition"
          value={settings.localization.currencyPosition}
          onChange={handleInputChange("localization", "currencyPosition")}
          options={[
            { value: "before", label: "Before ($100)" },
            { value: "after", label: "After (100$)" },
          ]}
        />
        <div className="grid grid-cols-2 gap-4">
          <SelectField
            label="Thousand Separator"
            name="thousandSeparator"
            value={settings.localization.thousandSeparator}
            onChange={handleInputChange("localization", "thousandSeparator")}
            options={[
              { value: ",", label: "Comma (1,000)" },
              { value: ".", label: "Period (1.000)" },
              { value: " ", label: "Space (1 000)" },
            ]}
          />
          <SelectField
            label="Decimal Separator"
            name="decimalSeparator"
            value={settings.localization.decimalSeparator}
            onChange={handleInputChange("localization", "decimalSeparator")}
            options={[
              { value: ".", label: "Period (10.99)" },
              { value: ",", label: "Comma (10,99)" },
            ]}
          />
        </div>
        <InputField
          label="Decimal Places"
          name="decimalPlaces"
          value={settings.localization.decimalPlaces}
          onChange={handleInputChange("localization", "decimalPlaces")}
          type="number"
          min="0"
          max="4"
        />
      </SectionCard>

      <SectionCard title="Units" description="Measurement units">
        <SelectField
          label="Weight Unit"
          name="weightUnit"
          value={settings.localization.weightUnit}
          onChange={handleInputChange("localization", "weightUnit")}
          options={[
            { value: "kg", label: "Kilograms (kg)" },
            { value: "lb", label: "Pounds (lb)" },
          ]}
        />
        <SelectField
          label="Dimension Unit"
          name="dimensionUnit"
          value={settings.localization.dimensionUnit}
          onChange={handleInputChange("localization", "dimensionUnit")}
          options={[
            { value: "cm", label: "Centimeters (cm)" },
            { value: "in", label: "Inches (in)" },
          ]}
        />
      </SectionCard>

      <SectionCard title="Week Settings" description="Calendar preferences">
        <SelectField
          label="First Day of Week"
          name="firstDayOfWeek"
          value={settings.localization.firstDayOfWeek}
          onChange={handleInputChange("localization", "firstDayOfWeek")}
          options={[
            { value: "monday", label: "Monday" },
            { value: "sunday", label: "Sunday" },
          ]}
        />
      </SectionCard>
    </div>
  );

  const renderSEOSettings = () => (
    <div className="space-y-6">
      <SectionCard title="Meta Tags" description="Default SEO metadata">
        <InputField
          label="Meta Title"
          name="metaTitle"
          value={settings.seo.metaTitle}
          onChange={handleInputChange("seo", "metaTitle")}
          maxLength="60"
          help={`${settings.seo.metaTitle?.length || 0}/60 characters`}
        />
        <TextAreaField
          label="Meta Description"
          name="metaDescription"
          value={settings.seo.metaDescription}
          onChange={handleInputChange("seo", "metaDescription")}
          rows={3}
          maxLength="160"
          help={`${settings.seo.metaDescription?.length || 0}/160 characters`}
        />
        <InputField
          label="Meta Keywords"
          name="metaKeywords"
          value={settings.seo.metaKeywords}
          onChange={handleInputChange("seo", "metaKeywords")}
          help="Comma-separated keywords"
        />
      </SectionCard>

      <SectionCard title="Social Media" description="Social sharing settings">
        <InputField
          label="OG Image URL"
          name="ogImage"
          value={settings.seo.ogImage}
          onChange={handleInputChange("seo", "ogImage")}
          type="url"
          help="Image for social media sharing (1200x630px)"
        />
        <SelectField
          label="Twitter Card"
          name="twitterCard"
          value={settings.seo.twitterCard}
          onChange={handleInputChange("seo", "twitterCard")}
          options={[
            { value: "summary", label: "Summary Card" },
            {
              value: "summary_large_image",
              label: "Summary Card with Large Image",
            },
          ]}
        />
        <InputField
          label="Twitter Handle"
          name="twitterHandle"
          value={settings.seo.twitterHandle}
          onChange={handleInputChange("seo", "twitterHandle")}
          placeholder="@username"
        />
      </SectionCard>

      <SectionCard title="Analytics" description="Tracking codes">
        <InputField
          label="Google Analytics ID"
          name="googleAnalyticsId"
          value={settings.seo.googleAnalyticsId}
          onChange={handleInputChange("seo", "googleAnalyticsId")}
          placeholder="G-XXXXXXXXXX"
        />
      </SectionCard>

      <SectionCard title="SEO Features" description="Additional SEO settings">
        <ToggleField
          label="Enable Sitemap"
          name="enableSitemap"
          checked={settings.seo.enableSitemap}
          onChange={handleInputChange("seo", "enableSitemap")}
          description="Generate sitemap.xml automatically"
        />
        <ToggleField
          label="Enable robots.txt"
          name="enableRobotsTxt"
          checked={settings.seo.enableRobotsTxt}
          onChange={handleInputChange("seo", "enableRobotsTxt")}
        />
        <InputField
          label="Canonical URL"
          name="canonicalUrl"
          value={settings.seo.canonicalUrl}
          onChange={handleInputChange("seo", "canonicalUrl")}
          type="url"
          help="Primary domain for canonical URLs"
        />
      </SectionCard>
    </div>
  );

  const renderPerformanceSettings = () => (
    <div className="space-y-6">
      <SectionCard title="Caching" description="Cache configuration">
        <ToggleField
          label="Enable Caching"
          name="enableCaching"
          checked={settings.performance.enableCaching}
          onChange={handleInputChange("performance", "enableCaching")}
        />
        {settings.performance.enableCaching && (
          <InputField
            label="Cache TTL (seconds)"
            name="cacheTTL"
            value={settings.performance.cacheTTL}
            onChange={handleInputChange("performance", "cacheTTL")}
            type="number"
            min="60"
            max="86400"
            help="How long to cache content (1 hour = 3600)"
          />
        )}
      </SectionCard>

      <SectionCard title="Optimization" description="Performance optimizations">
        <ToggleField
          label="Enable Compression"
          name="enableCompression"
          checked={settings.performance.enableCompression}
          onChange={handleInputChange("performance", "enableCompression")}
          description="Gzip/Brotli compression for faster loading"
        />
        <ToggleField
          label="Enable Minification"
          name="enableMinification"
          checked={settings.performance.enableMinification}
          onChange={handleInputChange("performance", "enableMinification")}
          description="Minify CSS, JavaScript, and HTML"
        />
        <ToggleField
          label="Enable Lazy Loading"
          name="enableLazyLoading"
          checked={settings.performance.enableLazyLoading}
          onChange={handleInputChange("performance", "enableLazyLoading")}
          description="Lazy load images and content"
        />
        <ToggleField
          label="Enable Pagination"
          name="enablePagination"
          checked={settings.performance.enablePagination}
          onChange={handleInputChange("performance", "enablePagination")}
        />
        {settings.performance.enablePagination && (
          <InputField
            label="Items Per Page"
            name="itemsPerPage"
            value={settings.performance.itemsPerPage}
            onChange={handleInputChange("performance", "itemsPerPage")}
            type="number"
            min="5"
            max="100"
          />
        )}
      </SectionCard>

      <SectionCard
        title="Image Optimization"
        description="Image handling settings"
      >
        <ToggleField
          label="Enable Image Optimization"
          name="enableImageOptimization"
          checked={settings.performance.enableImageOptimization}
          onChange={handleInputChange("performance", "enableImageOptimization")}
        />
        {settings.performance.enableImageOptimization && (
          <InputField
            label="Image Quality"
            name="imageQuality"
            value={settings.performance.imageQuality}
            onChange={handleInputChange("performance", "imageQuality")}
            type="number"
            min="10"
            max="100"
            help="Lower values = smaller files, lower quality"
          />
        )}
      </SectionCard>
    </div>
  );

  const renderBackupSettings = () => (
    <div className="space-y-6">
      <SectionCard
        title="Backup Schedule"
        description="Automatic backup configuration"
      >
        <ToggleField
          label="Enable Auto Backup"
          name="enableAutoBackup"
          checked={settings.backup.enableAutoBackup}
          onChange={handleInputChange("backup", "enableAutoBackup")}
        />
        {settings.backup.enableAutoBackup && (
          <>
            <SelectField
              label="Backup Frequency"
              name="backupFrequency"
              value={settings.backup.backupFrequency}
              onChange={handleInputChange("backup", "backupFrequency")}
              options={[
                { value: "hourly", label: "Hourly" },
                { value: "daily", label: "Daily" },
                { value: "weekly", label: "Weekly" },
              ]}
            />
            {settings.backup.backupFrequency === "daily" && (
              <InputField
                label="Backup Time"
                name="backupTime"
                value={settings.backup.backupTime}
                onChange={handleInputChange("backup", "backupTime")}
                type="time"
              />
            )}
            <InputField
              label="Backup Retention (days)"
              name="backupRetention"
              value={settings.backup.backupRetention}
              onChange={handleInputChange("backup", "backupRetention")}
              type="number"
              min="1"
              max="365"
              help="How long to keep backups before deleting"
            />
          </>
        )}
      </SectionCard>

      <SectionCard
        title="Backup Status"
        description="Recent backup information"
      >
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Last Backup:</span>
            <span className="font-medium">
              {settings.backup.lastBackup || "Never"}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Backup Size:</span>
            <span className="font-medium">{settings.backup.backupSize}</span>
          </div>
        </div>
        <button
          onClick={() => {
            setSettings((prev) => ({
              ...prev,
              backup: {
                ...prev.backup,
                lastBackup: new Date().toLocaleString(),
                backupSize: "15.2 MB",
              },
            }));
            setSuccess("Backup created successfully!");
            toastSuccess("Backup created successfully!");
            setTimeout(() => setSuccess(null), 3000);
          }}
          className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium flex items-center justify-center gap-2"
        >
          <Download size={16} />
          Create Backup Now
        </button>
      </SectionCard>
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case "general":
        return renderGeneralSettings();
      case "appearance":
        return renderAppearanceSettings();
      case "email":
        return renderEmailSettings();
      case "notifications":
        return renderNotificationSettings();
      case "security":
        return renderSecuritySettings();
      case "localization":
        return renderLocalizationSettings();
      case "seo":
        return renderSEOSettings();
      case "performance":
        return renderPerformanceSettings();
      case "backup":
        return renderBackupSettings();
      default:
        return renderGeneralSettings();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="relative">
            <Loader2
              size={48}
              className="animate-spin text-primary-500 mx-auto mb-4"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <Settings size={24} className="text-primary-300" />
            </div>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-4 font-medium">
            Loading settings...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 sm:p-4 md:p-6 border-0 sm:border border-gray-200 dark:border-gray-900 rounded-none sm:rounded-2xl shadow-none sm:shadow-lg overflow-x-hidden">
      <div className="space-y-4 sm:space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 bg-clip-text text-transparent">
              Settings
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Configure your application settings and preferences
            </p>
          </div>

          {/* Action buttons – wrap on mobile */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleExport}
              className="px-3 sm:px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 text-sm"
            >
              <Download size={16} className="sm:w-[18px] sm:h-[18px]" />
              <span className="hidden xs:inline">Export</span>
            </button>
            <label className="px-3 sm:px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 cursor-pointer text-sm">
              <Upload size={16} className="sm:w-[18px] sm:h-[18px]" />
              <span className="hidden xs:inline">Import</span>
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                className="hidden"
              />
            </label>
            <button
              onClick={handleReset}
              className="px-3 sm:px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 text-sm"
            >
              <RefreshCw size={16} className="sm:w-[18px] sm:h-[18px]" />
              <span className="hidden xs:inline">Reset</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 sm:px-6 py-2 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm text-sm ml-auto"
            >
              {saving ? (
                <Loader2
                  size={16}
                  className="animate-spin sm:w-[18px] sm:h-[18px]"
                />
              ) : (
                <Save size={16} className="sm:w-[18px] sm:h-[18px]" />
              )}
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        <AnimatePresence>
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-2xl flex items-center gap-3"
            >
              <CheckCircle className="text-green-500" size={20} />
              <p className="text-green-700 dark:text-green-300 text-sm flex-1">
                {success}
              </p>
              <button
                onClick={() => setSuccess(null)}
                className="p-1 hover:bg-green-100 rounded-lg"
              >
                <X size={16} className="text-green-500" />
              </button>
            </motion.div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl flex items-center gap-3"
            >
              <AlertCircle className="text-red-500" size={20} />
              <p className="text-red-700 dark:text-red-300 text-sm flex-1">
                {error}
              </p>
              <button
                onClick={() => setError(null)}
                className="p-1 hover:bg-red-100 rounded-lg"
              >
                <X size={16} className="text-red-500" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Settings Navigation and Content */}
        {/* Settings Navigation + Content */}
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* Mobile: horizontal scroll tabs | Desktop: sidebar */}
          <div className="lg:w-64 lg:flex-shrink-0">
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden lg:sticky lg:top-6">
              {/* Mobile horizontal scroll */}
              <nav className="flex lg:flex-col overflow-x-auto lg:overflow-visible p-2 gap-1 scrollbar-hide">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;

                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-shrink-0 lg:w-full flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                        isActive
                          ? "bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400 shadow-sm"
                          : "text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800"
                      }`}
                    >
                      <Icon size={18} className="flex-shrink-0" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Settings Content */}
          <div className="flex-1 min-w-0">
            <div className="space-y-4 sm:space-y-6">
              {/* Tab Description */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 sm:p-6">
                <div className="flex items-center gap-3">
                  {tabs.find((t) => t.id === activeTab)?.icon && (
                    <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-xl flex-shrink-0">
                      {React.createElement(
                        tabs.find((t) => t.id === activeTab).icon,
                        {
                          size: 22,
                          className: "text-primary-600 dark:text-primary-400",
                        },
                      )}
                    </div>
                  )}
                  <div className="min-w-0">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                      {tabs.find((t) => t.id === activeTab)?.label} Settings
                    </h2>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {tabs.find((t) => t.id === activeTab)?.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tab Content */}
              {renderActiveTab()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsManagement;
